
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { BigButton } from './components/BigButton';
import { HelpModal } from './components/HelpModal';
import { SettingsModal } from './components/SettingsModal';
import { 
  AspectRatio, 
  ASPECT_RATIOS, 
  PRESET_GREETINGS,
  NEW_YEAR_GREETINGS,
  HORSE_YEAR_GREETINGS,
  COLORS, 
  FONTS, 
  Point,
  FrameStyle,
  FrameSides
} from './types';

// 一鍵主題配置物件
const THEME = {
  app: {
    baseBg: 'bg-slate-50',
    panelBg: 'bg-white',
    panelBorder: 'border-slate-200',
  },
  primary: {
    header: 'bg-slate-900',
    text: 'text-indigo-600',
    border: 'border-indigo-600',
    buttonActive: 'bg-indigo-600 text-white border-indigo-700',
    buttonIdle: 'bg-white text-slate-700 border-slate-200',
    accent: 'accent-indigo-600',
  },
  frames: {
    bg: 'bg-slate-100',
    text: 'text-slate-800',
    border: 'border-slate-200',
    accent: 'accent-slate-700',
    buttonActive: 'bg-slate-800 text-white',
    buttonIdle: 'bg-white text-slate-700 border-slate-200',
    advancedToggle: 'text-slate-700',
  },
  greetings: {
    text: 'text-indigo-900',
    border: 'border-indigo-200',
    buttonActive: 'bg-indigo-600 text-white border-indigo-700',
    buttonIdle: 'bg-white text-slate-700 border-slate-200',
    inputBorder: 'border-slate-200',
  },
  date: {
    bg: 'bg-teal-50',
    text: 'text-teal-900',
    border: 'border-teal-200',
    accent: 'accent-teal-600',
    buttonActive: 'bg-teal-700 text-white border-teal-800',
    buttonIdle: 'bg-white text-slate-700 border-slate-200',
    advancedBg: 'bg-white/70',
    advancedToggle: 'bg-teal-100 text-teal-800',
  },
  advanced: {
    bg: 'bg-slate-100',
    text: 'text-slate-900',
    border: 'border-slate-200',
    accent: 'accent-indigo-600',
    buttonActive: 'bg-slate-800 text-white',
    buttonIdle: 'bg-white text-slate-700 border-slate-200',
    toggleBg: 'bg-slate-200 text-slate-800',
  }
};

const App: React.FC = () => {
  // 應用載入完成後隱藏載入指示器（確保最小顯示時間）
  useEffect(() => {
    // 使用統一的隱藏函數，確保最小顯示時間
    if (typeof window !== 'undefined' && (window as any).hideLoadingScreen) {
      (window as any).hideLoadingScreen();
    }
  }, []);
  
  const [deferredPrompt, setDeferredPrompt] = useState<any>(null);
  const [image, setImage] = useState<string | null>(null);
  const [aspectRatio, setAspectRatio] = useState<AspectRatio>('1:1');
  const [imageOffsetX, setImageOffsetX] = useState<number>(50);
  const [imageOffsetY, setImageOffsetY] = useState<number>(50);
  const [imageZoom, setImageZoom] = useState<number>(1);
  
  const [frameStyle, setFrameStyle] = useState<FrameStyle>('none');
  const [frameThicknessScale, setFrameThicknessScale] = useState<number>(5);
  const [frameSides, setFrameSides] = useState<FrameSides>({ top: true, bottom: true, left: true, right: true });
  const [frameColor, setFrameColor] = useState<string>('#FFFFFF');

  const [text, setText] = useState<string>('早安');
  const [isVertical, setIsVertical] = useState<boolean>(false);
  const [showDate, setShowDate] = useState<boolean>(true);
  
  // 祝福詞 Tab 切換（'daily' | 'newyear' | 'horse'）
  const [greetingTab, setGreetingTab] = useState<'daily' | 'newyear' | 'horse'>('daily');
  
  const [showAdvancedText, setShowAdvancedText] = useState<boolean>(false);
  const [showAdvancedImg, setShowAdvancedImg] = useState<boolean>(false);
  const [showAdvancedDate, setShowAdvancedDate] = useState<boolean>(false);

  // 整體文字設定
  const [borderThickness, setBorderThickness] = useState<number>(0.12);
  const [noBorder, setNoBorder] = useState<boolean>(false);
  const [hasShadow, setHasShadow] = useState<boolean>(true);
  const [hasGlow, setHasGlow] = useState<boolean>(false);
  const [letterSpacing, setLetterSpacing] = useState<number>(1.2); 
  const [textColor, setTextColor] = useState<string>('#FFD700');
  const [textScale, setTextScale] = useState<number>(1.5); 

  // 日期進階設定
  const [isDateStyleCustom, setIsDateStyleCustom] = useState<boolean>(false);
  const [dateColor, setDateColor] = useState<string>('#FFD700');
  const [dateBorderThickness, setDateBorderThickness] = useState<number>(0.12);
  const [dateNoBorder, setDateNoBorder] = useState<boolean>(false);
  const [dateScale, setDateScale] = useState<number>(1);
  const [dateLetterSpacing, setDateLetterSpacing] = useState<number>(1.1);

  const [exportUrl, setExportUrl] = useState<string | null>(null);

  const formatDateToChinese = (dateStr: string) => {
    if (!dateStr) return "";
    const [y, m, d] = dateStr.split('-');
    return `${parseInt(y)}年${parseInt(m)}月${parseInt(d)}日`;
  };

  const today = new Date();
  const todayStr = today.toISOString().split('T')[0];
  const [pickerDate, setPickerDate] = useState<string>(todayStr);
  const [customDate, setCustomDate] = useState<string>(formatDateToChinese(todayStr));

  const [fontFamily, setFontFamily] = useState<string>(FONTS[1].value); // 預設為明體（索引1）
  
  const [greetingPos, setGreetingPos] = useState<Point>({ x: 50, y: 70 });
  const [datePos, setDatePos] = useState<Point>({ x: 50, y: 90 });
  
  const [isExporting, setIsExporting] = useState<boolean>(false);
  const [isDragging, setIsDragging] = useState<'greeting' | 'date' | null>(null);
  const [showHelp, setShowHelp] = useState<boolean>(false);
  const [showSettings, setShowSettings] = useState<boolean>(false);
  const [hasEmojiWarning, setHasEmojiWarning] = useState<boolean>(false);
  
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const colorInputRef = useRef<HTMLInputElement>(null);
  const dateColorInputRef = useRef<HTMLInputElement>(null);
  const frameColorInputRef = useRef<HTMLInputElement>(null);

  const drawCanvas = useCallback(() => {
    if (!canvasRef.current || !image) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const img = new Image();
    img.crossOrigin = "anonymous";
    img.src = image;
    img.onload = async () => {
      const baseWidth = 1080;
      const ratio = ASPECT_RATIOS[aspectRatio];
      const baseHeight = baseWidth / ratio;
      
      canvas.width = baseWidth;
      canvas.height = baseHeight;

      const imgRatio = img.width / img.height;
      let sW, sH;
      if (imgRatio > ratio) {
        sH = img.height;
        sW = img.height * ratio;
      } else {
        sW = img.width;
        sH = img.width / ratio;
      }

      const zoomSW = sW / imageZoom;
      const zoomSH = sH / imageZoom;
      const maxOffsetX = img.width - zoomSW;
      const maxOffsetY = img.height - zoomSH;
      const sX = maxOffsetX * (imageOffsetX / 100);
      const sY = maxOffsetY * (imageOffsetY / 100);

      ctx.fillStyle = '#000000';
      ctx.fillRect(0, 0, baseWidth, baseHeight);
      ctx.drawImage(img, sX, sY, zoomSW, zoomSH, 0, 0, baseWidth, baseHeight);

      if (frameStyle !== 'none') {
        const frameThickness = baseWidth * (frameThicknessScale / 100);
        let finalFrameColor = frameColor;
        if (frameStyle === 'white') finalFrameColor = '#FFFFFF';
        if (frameStyle === 'black') finalFrameColor = '#000000';
        
        ctx.strokeStyle = finalFrameColor;
        ctx.lineWidth = frameThickness;
        ctx.lineCap = 'square';

        if (frameSides.top) {
          ctx.beginPath(); ctx.moveTo(0, frameThickness / 2); ctx.lineTo(baseWidth, frameThickness / 2); ctx.stroke();
        }
        if (frameSides.bottom) {
          ctx.beginPath(); ctx.moveTo(0, baseHeight - frameThickness / 2); ctx.lineTo(baseWidth, baseHeight - frameThickness / 2); ctx.stroke();
        }
        if (frameSides.left) {
          ctx.beginPath(); ctx.moveTo(frameThickness / 2, 0); ctx.lineTo(frameThickness / 2, baseHeight); ctx.stroke();
        }
        if (frameSides.right) {
          ctx.beginPath(); ctx.moveTo(baseWidth - frameThickness / 2, 0); ctx.lineTo(baseWidth - frameThickness / 2, baseHeight); ctx.stroke();
        }
      }

      const drawTextItem = async (txt: string, xPos: number, yPos: number, size: number, vertical: boolean, color: string, spacing: number, currentBorderThickness: number, currentNoBorder: boolean) => {
        ctx.save();
        // 圓體（Huninn）只有 400 字重，使用 400 避免回退到其他字體造成缺字
        const isRoundedFont = fontFamily.includes('Huninn');
        const fontWeight = isRoundedFont ? '400' : '900';
        ctx.font = `${fontWeight} ${size}px ${fontFamily}`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.lineJoin = 'round';

        const canvasX = (xPos / 100) * baseWidth;
        const canvasY = (yPos / 100) * baseHeight;
        const isRainbow = color.startsWith('rainbow');
        
        // 使用正確的分割方法（支援 emoji）
        const segments = segmentText(txt);
        const step = size * spacing;
        const spaceStep = size * spacing * 0.3; // 空格使用較小的間距（30%）

        // 計算總寬度（考慮空格的小間距）
        let totalWidth = 0;
        for (const seg of segments) {
          totalWidth += seg.trim() === '' ? spaceStep : step;
        }

        // 繪製每個字符（包括空格和 emoji）
        let currentOffset = -totalWidth / 2;
        for (let i = 0; i < segments.length; i++) {
          const segment = segments[i];
          const isSpace = segment.trim() === '';
          const currentStep = isSpace ? spaceStep : step;
          
          let px = canvasX;
          let py = canvasY;
          if (vertical) {
            py = canvasY + currentOffset + currentStep / 2;
          } else {
            px = canvasX + currentOffset + currentStep / 2;
          }
          currentOffset += currentStep;

          // 如果是空格，繪製空格
          if (isSpace) {
            // 空格使用較小的字體大小
            ctx.save();
            const isRoundedFont = fontFamily.includes('Huninn');
            const fontWeight = isRoundedFont ? '400' : '900';
            ctx.font = `${fontWeight} ${size * 0.3}px ${fontFamily}`;
            ctx.fillStyle = 'transparent'; // 空格不顯示顏色，但占用位置
            ctx.fillText(' ', px, py);
            ctx.restore();
            continue;
          }

          // 如果是 emoji，使用 Twemoji 渲染
          if (isEmoji(segment)) {
            const emojiUrl = getEmojiImageUrl(segment);
            if (emojiUrl) {
              try {
                const emojiImg = new Image();
                emojiImg.crossOrigin = 'anonymous';
                await new Promise((resolve, reject) => {
                  emojiImg.onload = () => {
                    // 繪製 emoji 圖片
                    const emojiSize = size * 1.2; // emoji 稍微大一點
                    ctx.save();
                    
                    // 應用陰影效果
                    if (hasShadow) {
                      ctx.shadowColor = 'rgba(0, 0, 0, 0.8)';
                      ctx.shadowBlur = size * 0.1;
                      ctx.shadowOffsetX = size * 0.04;
                      ctx.shadowOffsetY = size * 0.04;
                    }
                    
                    ctx.drawImage(
                      emojiImg,
                      px - emojiSize / 2,
                      py - emojiSize / 2,
                      emojiSize,
                      emojiSize
                    );
                    ctx.restore();
                    resolve(null);
                  };
                  emojiImg.onerror = reject;
                  emojiImg.src = emojiUrl;
                });
                continue; // 跳過文字渲染
              } catch (e) {
                // 如果 emoji 載入失敗，繼續使用文字渲染
                console.warn('Emoji 載入失敗，使用文字渲染:', segment);
              }
            }
          }

          // 普通文字渲染（原有邏輯）
          if (hasShadow) {
            ctx.save();
            ctx.shadowColor = 'rgba(0, 0, 0, 0.8)';
            ctx.shadowBlur = size * 0.1;
            ctx.shadowOffsetX = size * 0.04;
            ctx.shadowOffsetY = size * 0.04;
            
            if (!currentNoBorder) {
              ctx.lineWidth = size * currentBorderThickness;
              ctx.strokeText(segment, px, py);
            }
            ctx.fillText(segment, px, py);
            ctx.restore();
          }

          if (hasGlow) {
            ctx.save();
            ctx.shadowColor = 'rgba(255, 255, 255, 1)';
            ctx.shadowBlur = size * 0.15;
            ctx.strokeStyle = 'white';
            ctx.lineWidth = size * (currentBorderThickness + 0.03);
            ctx.strokeText(segment, px, py);
            ctx.restore();
          }

          if (!currentNoBorder) {
            ctx.save();
            let strokeColor = (color === '#000000' || color === 'rainbow-white') ? '#FFFFFF' : '#000000';
            ctx.strokeStyle = strokeColor;
            ctx.lineWidth = size * currentBorderThickness;
            ctx.strokeText(segment, px, py);
            ctx.restore();
          }

          if (isRainbow) {
            const rainbowGrad = ctx.createLinearGradient(px - size/2, py - size/2, px + size/2, py + size/2);
            ['#ff0000', '#ffff00', '#00ff00', '#00ffff', '#0000ff', '#ff00ff'].forEach((c, idx) => rainbowGrad.addColorStop(idx/5, c));
            ctx.fillStyle = rainbowGrad;
          } else {
            ctx.fillStyle = color;
          }
          ctx.fillText(segment, px, py);
        }
        ctx.restore();
      };

      await drawTextItem(text, greetingPos.x, greetingPos.y, baseWidth * 0.1 * textScale, isVertical, textColor, letterSpacing, borderThickness, noBorder);
      
      if (showDate) {
        // 日期顏色完全獨立，直接使用 dateColor（包含彩虹漸層）
        const dColor = dateColor;
        // 進階設定：只要有任何一個進階設定被修改過，就使用進階設定
        const hasAdvancedSettings = dateScale !== 1 || dateBorderThickness !== borderThickness || dateLetterSpacing !== 1.1 || dateNoBorder !== noBorder;
        const dScale = hasAdvancedSettings ? dateScale : 1;
        const dBorderT = hasAdvancedSettings ? dateBorderThickness : borderThickness;
        const dNoBorder = hasAdvancedSettings ? dateNoBorder : noBorder;
        const dSpacing = hasAdvancedSettings ? dateLetterSpacing : 1.1;
        await drawTextItem(customDate, datePos.x, datePos.y, baseWidth * 0.045 * dScale, false, dColor, dSpacing, dBorderT, dNoBorder);
      }
    };
    img.onerror = () => {};
  }, [image, aspectRatio, imageOffsetX, imageOffsetY, imageZoom, frameStyle, frameThicknessScale, frameSides, frameColor, text, isVertical, showDate, customDate, textColor, fontFamily, greetingPos, datePos, textScale, borderThickness, noBorder, hasShadow, hasGlow, letterSpacing, dateColor, dateBorderThickness, dateNoBorder, dateScale, dateLetterSpacing]);

  // PWA 安裝提示監聽
  useEffect(() => {
    const handleBeforeInstallPrompt = (e: Event) => {
      // 阻止瀏覽器預設的安裝提示
      e.preventDefault();
      // 保存事件，以便稍後觸發
      setDeferredPrompt(e);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  useEffect(() => {
    drawCanvas();
  }, [drawCanvas]);

  const handlePointerDown = (e: React.PointerEvent) => {
    if (!canvasRef.current) return;
    const rect = canvasRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    const distGreeting = Math.hypot(x - greetingPos.x, y - greetingPos.y);
    const distDate = Math.hypot(x - datePos.x, y - datePos.y);
    if (showDate && distDate < distGreeting && distDate < 15) setIsDragging('date');
    else if (distGreeting < 20) setIsDragging('greeting');
  };

  const handlePointerMove = (e: React.PointerEvent) => {
    if (!isDragging || !canvasRef.current) return;
    const rect = canvasRef.current.getBoundingClientRect();
    const x = Math.max(0, Math.min(100, ((e.clientX - rect.left) / rect.width) * 100));
    const y = Math.max(0, Math.min(100, ((e.clientY - rect.top) / rect.height) * 100));
    if (isDragging === 'greeting') setGreetingPos({ x, y });
    else if (isDragging === 'date') setDatePos({ x, y });
  };

  const handleShare = async () => {
    if (!exportUrl) return;
    if (navigator.share) {
      try {
        const response = await fetch(exportUrl);
        const blob = await response.blob();
        const file = new File([blob], `good-morning-${new Date().getTime()}.jpg`, { type: 'image/jpeg' });
        await navigator.share({
          files: [file],
          title: '送你一張早安圖',
          text: '祝你今天事事順心！',
        });
      } catch (err) {
        // 如果分享失敗，嘗試下載
        handleDownload();
      }
    } else {
      // 如果不支援分享，直接下載
      handleDownload();
    }
  };

  const handleDownload = async () => {
    if (!exportUrl) return;
    
    // LINE 瀏覽器特殊處理：使用新窗口打開圖片
    if (isLineBrowser) {
      try {
        const newWindow = window.open();
        if (newWindow) {
          newWindow.document.write(`
            <html>
              <head>
                <title>早安圖</title>
                <meta name="viewport" content="width=device-width, initial-scale=1.0">
                <style>
                  body {
                    margin: 0;
                    padding: 20px;
                    background: #000;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    min-height: 100vh;
                  }
                  img {
                    max-width: 100%;
                    height: auto;
                    border-radius: 20px;
                  }
                  p {
                    color: white;
                    text-align: center;
                    margin-top: 20px;
                    font-size: 18px;
                    font-weight: bold;
                  }
                </style>
              </head>
              <body>
                <img src="${exportUrl}" alt="早安圖" />
                <p>長按圖片選擇「儲存圖片」</p>
              </body>
            </html>
          `);
          newWindow.document.close();
          return;
        }
      } catch (err) {
        // 如果新窗口失敗，繼續使用其他方法
      }
    }
    
    try {
      // 手機和電腦通用下載方案
      const response = await fetch(exportUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      
      // 創建下載連結
      const link = document.createElement('a');
      link.href = url;
      link.download = `good-morning-${new Date().getTime()}.jpg`;
      link.style.display = 'none';
      
      // 添加到頁面並觸發下載
      document.body.appendChild(link);
      link.click();
      
      // 清理
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 100);
    } catch (err) {
      // 備案：直接使用 dataURL（適用於小圖片）
      try {
        const link = document.createElement('a');
        link.href = exportUrl;
        link.download = `good-morning-${new Date().getTime()}.jpg`;
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        setTimeout(() => document.body.removeChild(link), 100);
      } catch (fallbackErr) {
        // 最後備案：提示用戶長按圖片保存
        if (isLineBrowser) {
          alert('在 LINE 瀏覽器中，請長按預覽圖片選擇「儲存圖片」或「下載圖片」。\n\n如果無法儲存，請嘗試：\n1. 點擊右上角「...」→「在瀏覽器中開啟」\n2. 或使用其他瀏覽器（Chrome、Safari）開啟此網站');
        } else {
          alert('請長按圖片選擇「儲存圖片」或「下載圖片」');
        }
      }
    }
  };

  const isPredefinedColor = COLORS.some(c => c.value === textColor);
  const isDatePredefinedColor = COLORS.some(c => c.value === dateColor);

  const ColorButton = ({ color, isActive, onClick }: { color: string, isActive: boolean, onClick: () => void }) => {
    const isRainbow = color.startsWith('rainbow');
    let borderClass = "border-2 border-[#c2c2c2]";
    if (color === 'rainbow-black') borderClass = "border-2 border-black";
    if (color === 'rainbow-white') borderClass = "border-2 border-white";
    
    return (
      <button 
        onClick={onClick} 
        className={`h-16 rounded-xl transition-all ${isActive ? 'border-4 border-indigo-600 scale-110 z-10 shadow-lg' : borderClass}`} 
        style={{ background: isRainbow ? 'linear-gradient(45deg, red, yellow, green, blue, purple)' : color }} 
      />
    );
  };

  // 判斷是否為手機/移動裝置，用於切換分享與下載按鈕
  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
  
  // 檢測是否在 LINE 瀏覽器中
  const isLineBrowser = /Line/i.test(navigator.userAgent);
  
  // 檢測是否為 Android LINE 瀏覽器
  const isAndroidLineBrowser = /Android/i.test(navigator.userAgent) && /Line/i.test(navigator.userAgent);
  
  // Android LINE 瀏覽器提示狀態
  const [showAndroidLineWarning, setShowAndroidLineWarning] = useState<boolean>(true);
  
  // 檢測文字中是否包含 emoji
  const hasEmoji = (str: string): boolean => {
    const emojiRegex = /[\u{1F300}-\u{1F9FF}]|[\u{2600}-\u{26FF}]|[\u{2700}-\u{27BF}]|[\u{1F600}-\u{1F64F}]|[\u{1F680}-\u{1F6FF}]|[\u{1F900}-\u{1F9FF}]|[\u{1FA00}-\u{1FA6F}]|[\u{1FA70}-\u{1FAFF}]|[\u{200D}]|[\u{FE00}-\u{FE0F}]|[\u{203C}]|[\u{2049}]|[\u{2122}]|[\u{2139}]|[\u{2194}-\u{2199}]|[\u{21A9}-\u{21AA}]|[\u{231A}-\u{231B}]|[\u{2328}]|[\u{23CF}]|[\u{23E9}-\u{23F3}]|[\u{23F8}-\u{23FA}]|[\u{24C2}]|[\u{25AA}-\u{25AB}]|[\u{25B6}]|[\u{25C0}]|[\u{25FB}-\u{25FE}]|[\u{2600}-\u{2604}]|[\u{260E}]|[\u{2611}]|[\u{2614}-\u{2615}]|[\u{2618}]|[\u{261D}]|[\u{2620}]|[\u{2622}-\u{2623}]|[\u{2626}]|[\u{262A}]|[\u{262E}-\u{262F}]|[\u{2638}-\u{263A}]|[\u{2640}]|[\u{2642}]|[\u{2648}-\u{2653}]|[\u{2660}]|[\u{2663}]|[\u{2665}-\u{2666}]|[\u{2668}]|[\u{267B}]|[\u{267F}]|[\u{2692}-\u{2697}]|[\u{2699}]|[\u{269B}-\u{269C}]|[\u{26A0}-\u{26A1}]|[\u{26AA}-\u{26AB}]|[\u{26B0}-\u{26B1}]|[\u{26BD}-\u{26BE}]|[\u{26C4}-\u{26C5}]|[\u{26C8}]|[\u{26CE}-\u{26CF}]|[\u{26D1}]|[\u{26D3}-\u{26D4}]|[\u{26E9}-\u{26EA}]|[\u{26F0}-\u{26F5}]|[\u{26F7}-\u{26FA}]|[\u{26FD}]|[\u{2702}]|[\u{2705}]|[\u{2708}-\u{270D}]|[\u{270F}]|[\u{2712}]|[\u{2714}]|[\u{2716}]|[\u{271D}]|[\u{2721}]|[\u{2728}]|[\u{2733}-\u{2734}]|[\u{2744}]|[\u{2747}]|[\u{274C}]|[\u{274E}]|[\u{2753}-\u{2755}]|[\u{2757}]|[\u{2763}-\u{2764}]|[\u{2795}-\u{2797}]|[\u{27A1}]|[\u{27B0}]|[\u{27BF}]|[\u{2934}-\u{2935}]|[\u{2B05}-\u{2B07}]|[\u{2B1B}-\u{2B1C}]|[\u{2B50}]|[\u{2B55}]|[\u{3030}]|[\u{303D}]|[\u{3297}]|[\u{3299}]/u;
    return emojiRegex.test(str);
  };
  
  // 正確分割文字（支援 emoji 和複合字符）
  const segmentText = (text: string): string[] => {
    // 優先使用 Intl.Segmenter（現代瀏覽器）
    if (typeof Intl !== 'undefined' && 'Segmenter' in Intl) {
      try {
        const segmenter = new Intl.Segmenter('zh', { granularity: 'grapheme' });
        return Array.from(segmenter.segment(text), s => s.segment);
      } catch (e) {
        // 如果失敗，使用備案方案
      }
    }
    
    // 備案方案：使用正則表達式分割（支援大部分 emoji）
    const graphemeRegex = /[\uD800-\uDBFF][\uDC00-\uDFFF]|[\u2600-\u27BF]|[\u{1F300}-\u{1F9FF}]|[\u{1FA00}-\u{1FAFF}]|[\u{200D}]|[\u{FE00}-\u{FE0F}]|./gu;
    return text.match(graphemeRegex) || [];
  };
  
  // 檢測是否為 emoji 字符
  const isEmoji = (char: string): boolean => {
    return hasEmoji(char);
  };
  
  // 使用 Twemoji 將 emoji 轉換為圖片 URL
  const getEmojiImageUrl = (emoji: string): string | null => {
    if (typeof window === 'undefined' || !(window as any).twemoji) {
      return null;
    }
    try {
      // 使用 Twemoji 的 convert API 獲取 emoji 的 Unicode 碼點
      const codePoint = (window as any).twemoji.convert.toCodePoint(emoji);
      if (!codePoint) return null;
      
      // 構建 Twemoji CDN URL (使用 SVG 格式，更清晰)
      return `https://cdn.jsdelivr.net/gh/twitter/twemoji@14.0.2/assets/svg/${codePoint}.svg`;
    } catch (e) {
      // 備案：使用 parse 方法
      try {
        const parsed = (window as any).twemoji.parse(emoji, {
          folder: 'svg',
          ext: '.svg',
          base: 'https://cdn.jsdelivr.net/gh/twitter/twemoji@14.0.2/assets/'
        });
        const match = parsed.match(/src="([^"]+)"/);
        return match ? match[1] : null;
      } catch (e2) {
        return null;
      }
    }
  };
  
  // 圖片壓縮函數（限制最大尺寸和文件大小）
  const compressImage = (file: File, maxWidth: number = 4096, maxHeight: number = 4096, quality: number = 0.9): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          let width = img.width;
          let height = img.height;
          
          // 如果圖片太大，進行縮放
          if (width > maxWidth || height > maxHeight) {
            const ratio = Math.min(maxWidth / width, maxHeight / height);
            width = width * ratio;
            height = height * ratio;
          }
          
          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          if (!ctx) {
            reject(new Error('無法創建畫布'));
            return;
          }
          
          ctx.drawImage(img, 0, 0, width, height);
          
          // 嘗試壓縮，如果文件還是太大，降低質量
          let currentQuality = quality;
          const tryCompress = () => {
            const dataUrl = canvas.toDataURL('image/jpeg', currentQuality);
            const sizeInMB = (dataUrl.length * 3) / 4 / 1024 / 1024; // 估算大小
            
            // 如果超過 10MB，降低質量重試
            if (sizeInMB > 10 && currentQuality > 0.3) {
              currentQuality -= 0.1;
              tryCompress();
            } else {
              resolve(dataUrl);
            }
          };
          
          tryCompress();
        };
        img.onerror = () => reject(new Error('圖片載入失敗'));
        img.src = e.target?.result as string;
      };
      reader.onerror = () => reject(new Error('文件讀取失敗'));
      reader.readAsDataURL(file);
    });
  };

  return (
    <div className={`h-screen w-screen flex flex-col ${THEME.app.baseBg} font-bold select-none overflow-hidden touch-none`}>
      {/* Android LINE 瀏覽器警告橫幅 */}
      {isAndroidLineBrowser && showAndroidLineWarning && (
        <div className="bg-orange-500 text-white px-4 py-3 flex items-center justify-between gap-4 z-50 shrink-0">
          <div className="flex-1">
            <div className="font-black text-sm mb-1">⚠️ Android LINE 瀏覽器無法儲存圖片</div>
            <div className="text-xs opacity-90">請使用 Google Chrome 或其他瀏覽器開啟此網站，即可正常儲存圖片</div>
          </div>
          <button
            onClick={() => setShowAndroidLineWarning(false)}
            className="text-white hover:bg-white/20 rounded-full p-1 transition-colors shrink-0"
            aria-label="關閉提示"
          >
            ✕
          </button>
        </div>
      )}
      
      {/* 圖片預覽區 */}
      <div className={`h-[60vh] bg-slate-900 flex flex-col relative shadow-inner overflow-hidden shrink-0`}>
        <header className={`${THEME.primary.header} text-white p-2 shadow-md z-10 shrink-0 flex items-center justify-center relative`}>
          <button 
            onClick={() => setShowHelp(true)}
            className="absolute left-4 text-2xl hover:scale-110 transition-transform active:scale-95 w-10 h-10 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20"
            aria-label="說明"
          >
            ?
          </button>
          <h1 className="text-xl font-black tracking-widest">早安圖大師 MGPICMAKER</h1>
          <button 
            onClick={() => setShowSettings(true)}
            className="absolute right-4 text-2xl hover:scale-110 transition-transform active:scale-95 w-10 h-10 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20"
            aria-label="設定"
          >
            ⚙️
          </button>
        </header>
        <div className="flex-1 flex items-center justify-center p-0 relative min-h-0 bg-slate-800">
          {!image ? (
            <div className="text-center text-white p-4">
              <BigButton onClick={() => fileInputRef.current?.click()} className="bg-indigo-600 py-10 px-16 text-3xl shadow-2xl">選取照片</BigButton>
              <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={async (e) => {
                const file = e.target.files?.[0];
                if (file) {
                  try {
                    // 壓縮圖片以避免內存問題
                    const compressedDataUrl = await compressImage(file);
                    setImage(compressedDataUrl);
                  } catch (err) {
                    // 如果壓縮失敗，使用原始方式
                    const reader = new FileReader();
                    reader.onload = (ev) => setImage(ev.target?.result as string);
                    reader.readAsDataURL(file);
                  }
                }
              }} />
            </div>
          ) : (
            <div className="relative w-full h-full flex items-center justify-center touch-none overflow-hidden"
              onPointerDown={handlePointerDown} onPointerMove={handlePointerMove} onPointerUp={() => setIsDragging(null)} onPointerLeave={() => setIsDragging(null)}>
              <canvas ref={canvasRef} className="max-w-full max-h-full shadow-2xl bg-black object-contain" />
              {isDragging && (
                <div className="absolute top-4 left-1/2 -translate-x-1/2 bg-indigo-600/95 text-white px-6 py-3 rounded-full text-lg shadow-lg z-20 backdrop-blur-sm pointer-events-none flex items-center gap-2 font-black">
                  <span className="text-2xl animate-bounce">📍</span>
                  <span>移動中... 手指拖動位置</span>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* 控制面板區 */}
      <div className={`h-[40vh] overflow-y-auto overflow-x-hidden ${THEME.app.panelBg} border-t-8 ${THEME.app.panelBorder} p-6 space-y-10 scroll-smooth shadow-[0_-10px_20px_rgba(0,0,0,0.05)]`}>
        {!image ? (
          <>
            {/* 公告區塊 */}
            <div className="bg-amber-50 border-2 border-red-400 rounded-2xl p-6 space-y-4">
              <div className="flex items-center gap-2 mb-3">
                <span className="text-2xl">📢</span>
                <h3 className="text-xl font-black text-red-600">最新公告</h3>
              </div>
              <div className="space-y-3 text-slate-800">
                <div className="flex items-start gap-2">
                  <span className="text-xl">✨</span>
                  <p className="text-red-600 font-bold flex-1">目前版本已新增年節祝賀文、馬年祝賀文字!</p>
                </div>
                <div className="flex items-start gap-2">
                  <span className="text-xl">💡</span>
                  <p className="text-slate-800 font-bold flex-1">把網頁加入手機主畫面吧!</p>
                </div>
              </div>
            </div>
          </>
        ) : (
          <>
            {/* 比例與邊框區 */}
            <section className="space-y-4">
              <h2 className={`text-2xl ${THEME.primary.text} border-l-8 ${THEME.primary.border} pl-4`}>比例與邊框</h2>
              <div className="grid grid-cols-4 gap-2">
                {(Object.keys(ASPECT_RATIOS) as AspectRatio[]).map(r => (
                  <button key={r} onClick={() => setAspectRatio(r)} className={`p-4 rounded-xl border-2 ${aspectRatio === r ? THEME.primary.buttonActive : THEME.primary.buttonIdle}`}>{r}</button>
                ))}
              </div>
              <div className={`${THEME.frames.bg} p-6 rounded-3xl space-y-6`}>
                <div>
                  <label className={`block text-xl mb-3 ${THEME.frames.text}`}>邊框樣式：</label>
                  <div className="grid grid-cols-4 gap-2">
                    <button onClick={() => setFrameStyle('none')} className={`p-4 rounded-xl border-2 ${frameStyle === 'none' ? THEME.frames.buttonActive : THEME.frames.buttonIdle}`}>無</button>
                    <button onClick={() => setFrameStyle('white')} className={`p-4 rounded-xl border-2 ${frameStyle === 'white' ? THEME.frames.buttonActive : THEME.frames.buttonIdle}`}>白</button>
                    <button onClick={() => setFrameStyle('black')} className={`p-4 rounded-xl border-2 ${frameStyle === 'black' ? THEME.frames.buttonActive : THEME.frames.buttonIdle}`}>黑</button>
                    <label 
                      className={`p-4 rounded-xl border-2 flex flex-col items-center justify-center cursor-pointer ${frameStyle === 'custom' ? THEME.frames.buttonActive : THEME.frames.buttonIdle} active:scale-95 transition-transform relative overflow-hidden`}
                    >
                      <input
                        type="color"
                        ref={frameColorInputRef}
                        value={frameColor}
                        onChange={(e) => {setFrameColor(e.target.value); setFrameStyle('custom')}}
                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                        style={{ WebkitAppearance: 'none', appearance: 'none', border: 'none' }}
                      />
                      <span className="pointer-events-none">🎨</span>
                      <div className="w-4 h-4 rounded-full mt-1 border border-slate-300 pointer-events-none" style={{ backgroundColor: frameColor }} />
                    </label>
                  </div>
                </div>
                {frameStyle !== 'none' && (
                  <div className="space-y-4 p-4 bg-white/50 rounded-2xl">
                    <div className="grid grid-cols-4 gap-2">
                      {['top', 'bottom', 'left', 'right'].map((side) => (
                        <button key={side} onClick={() => setFrameSides(prev => ({...prev, [side]: !prev[side as keyof FrameSides]}))} className={`p-3 rounded-lg border-2 ${frameSides[side as keyof FrameSides] ? THEME.frames.buttonActive : THEME.frames.buttonIdle}`}>
                          {side === 'top' ? '上' : side === 'bottom' ? '下' : side === 'left' ? '左' : '右'}
                        </button>
                      ))}
                    </div>
                    <label className={`block ${THEME.frames.text}`}>邊框粗細：<input type="range" min="1" max="15" step="0.5" value={frameThicknessScale} onChange={e => setFrameThicknessScale(Number(e.target.value))} className={`w-full h-10 ${THEME.frames.accent}`} /></label>
                  </div>
                )}
                <div className={`border-t ${THEME.frames.border} pt-4`}>
                  <button onClick={() => setShowAdvancedImg(!showAdvancedImg)} className={`${THEME.frames.advancedToggle} text-lg mb-2 font-black flex items-center gap-2`}>
                    {showAdvancedImg ? '▼ 隱藏照片微調' : '▶ 進階照片縮放與位移'}
                  </button>
                  {showAdvancedImg && (
                    <div className="space-y-6 mt-4 p-4 bg-white/40 rounded-2xl border border-slate-200">
                      <label className="block text-slate-700 font-bold">🔍 照片縮放：<input type="range" min="1" max="4" step="0.05" value={imageZoom} onChange={e => setImageZoom(Number(e.target.value))} className={`w-full h-10 ${THEME.primary.accent || 'accent-indigo-600'}`} /></label>
                      <label className="block text-slate-700 font-bold">↔️ 左右移動：<input type="range" min="0" max="100" value={imageOffsetX} onChange={e => setImageOffsetX(Number(e.target.value))} className={`w-full h-10 ${THEME.primary.accent || 'accent-indigo-600'}`} /></label>
                      <label className="block text-slate-700 font-bold">↕️ 上下移動：<input type="range" min="0" max="100" value={imageOffsetY} onChange={e => setImageOffsetY(Number(e.target.value))} className={`w-full h-10 ${THEME.primary.accent || 'accent-indigo-600'}`} /></label>
                    </div>
                  )}
                </div>
              </div>
            </section>

            {/* 祝賀語區 */}
            <section className="space-y-6">
              <h2 className={`text-2xl ${THEME.primary.text} border-l-8 ${THEME.primary.border} pl-4`}>💬 祝賀語</h2>
              
              {/* Tab 切換 */}
              <div className="flex gap-2 border-b-2 border-slate-200">
                <button
                  onClick={() => setGreetingTab('daily')}
                  className={`px-6 py-3 font-black text-lg rounded-t-xl transition-all ${
                    greetingTab === 'daily'
                      ? `${THEME.greetings.buttonActive} border-b-4 border-indigo-600`
                      : `${THEME.greetings.buttonIdle} hover:bg-slate-50`
                  }`}
                >
                  日常
                </button>
                <button
                  onClick={() => setGreetingTab('newyear')}
                  className={`px-6 py-3 font-black text-lg rounded-t-xl transition-all ${
                    greetingTab === 'newyear'
                      ? `${THEME.greetings.buttonActive} border-b-4 border-indigo-600`
                      : `${THEME.greetings.buttonIdle} hover:bg-slate-50`
                  }`}
                >
                  年節賀詞
                </button>
                <button
                  onClick={() => setGreetingTab('horse')}
                  className={`px-6 py-3 font-black text-lg rounded-t-xl transition-all ${
                    greetingTab === 'horse'
                      ? `${THEME.greetings.buttonActive} border-b-4 border-indigo-600`
                      : `${THEME.greetings.buttonIdle} hover:bg-slate-50`
                  }`}
                >
                  馬年吉祥話
                </button>
              </div>
              
              {/* 祝福詞按鈕網格 */}
              <div className="grid grid-cols-3 gap-2">
                {(greetingTab === 'daily' ? PRESET_GREETINGS : 
                  greetingTab === 'newyear' ? NEW_YEAR_GREETINGS : 
                  HORSE_YEAR_GREETINGS).map(g => (
                  <button 
                    key={g} 
                    onClick={() => setText(g)} 
                    className={`p-4 rounded-xl border-2 text-sm ${text === g ? THEME.greetings.buttonActive : THEME.greetings.buttonIdle}`}
                  >
                    {g}
                  </button>
                ))}
              </div>
              <div className="space-y-2">
                <input value={text} onChange={e => {
                  const newText = e.target.value;
                  setText(newText);
                  setHasEmojiWarning(hasEmoji(newText) || hasEmoji(customDate));
                }} className={`w-full p-6 text-3xl rounded-2xl border-2 ${hasEmojiWarning ? 'border-blue-500' : THEME.greetings.inputBorder} outline-none shadow-inner font-black ${THEME.greetings.text}`} />
                {hasEmojiWarning && (
                  <div className="bg-blue-50 border-2 border-blue-500 rounded-xl p-4 text-blue-900 text-sm font-bold space-y-2">
                    <div className="flex items-start gap-2">
                      <span className="text-xl">💡</span>
                      <div className="flex-1">
                        <div className="font-black text-base mb-1">已偵測到表情符號（Emoji）</div>
                        <div className="text-sm leading-relaxed">
                          我們會自動使用 Twitter Emoji 來正確顯示表情符號，確保在所有裝置上都能正常顯示。如果遇到某些特殊表情符號無法顯示，請嘗試移除或更換。
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </section>

            {/* 祝賀語顏色與字體區 */}
            <section className="space-y-6">
              <h2 className={`text-2xl ${THEME.primary.text} border-l-8 ${THEME.primary.border} pl-4`}>💬 祝賀語顏色與字體</h2>
              <div>
                <label className={`block text-lg mb-3 ${THEME.primary.text} font-bold`}>顏色：</label>
                <div className="grid grid-cols-5 gap-3">
                  {COLORS.map(c => (
                    <ColorButton key={c.value} color={c.value} isActive={textColor === c.value} onClick={() => setTextColor(c.value)} />
                  ))}
                  <label 
                    className={`h-16 rounded-xl border-4 flex items-center justify-center bg-white cursor-pointer ${!isPredefinedColor ? 'border-indigo-600 shadow-md' : 'border-[#c2c2c2]'} active:scale-95 transition-transform relative overflow-hidden`}
                  >
                    <input
                      type="color"
                      ref={colorInputRef}
                      value={isPredefinedColor ? '#000000' : textColor}
                      onChange={(e) => setTextColor(e.target.value)}
                      className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                      style={{ WebkitAppearance: 'none', appearance: 'none', border: 'none' }}
                    />
                    <span className="text-3xl pointer-events-none" style={{ color: !isPredefinedColor ? textColor : '#666' }}>🎨</span>
                  </label>
                </div>
              </div>
              <div>
                <label className={`block text-lg mb-3 ${THEME.primary.text} font-bold`}>字體：</label>
                <div className="grid grid-cols-2 gap-3">
                  {FONTS.map(f => (
                    <button key={f.value} onClick={() => setFontFamily(f.value)} className={`p-5 rounded-xl border-2 text-xl ${fontFamily === f.value ? THEME.greetings.buttonActive : 'bg-slate-50 text-slate-700'}`} style={{fontFamily: f.value}}>{f.name}</button>
                  ))}
                </div>
              </div>
            </section>

            {/* 日期標籤區 */}
            <section className={`${THEME.date.bg} p-6 rounded-3xl border-2 ${THEME.date.border} space-y-4`}>
               <h2 className={`text-2xl ${THEME.date.text}`}>📅 日期標籤</h2>
               <div className="flex flex-col md:flex-row gap-4">
                  <input 
                    type="date" 
                    value={pickerDate} 
                    onChange={e => {
                      setPickerDate(e.target.value);
                      setCustomDate(formatDateToChinese(e.target.value));
                    }} 
                    className={`flex-1 p-4 rounded-xl border-2 text-xl font-bold bg-white outline-indigo-500 ${THEME.date.border}`}
                  />
                  <button onClick={() => setShowDate(!showDate)} className={`px-6 py-4 rounded-xl border-2 whitespace-nowrap ${showDate ? THEME.date.buttonActive : THEME.date.buttonIdle}`}>
                    {showDate ? '✅ 顯示' : '❌ 隱藏'}
                  </button>
               </div>
               <div className="flex flex-col md:flex-row md:items-center gap-2">
                 <span className={`${THEME.date.text} shrink-0 font-black`}>內容：</span>
                 <input value={customDate} onChange={e => {
                   const newDate = e.target.value;
                   setCustomDate(newDate);
                   setHasEmojiWarning(hasEmoji(text) || hasEmoji(newDate));
                 }} className={`flex-1 p-4 rounded-xl border-2 text-lg font-bold ${hasEmojiWarning ? 'border-blue-500' : THEME.date.border}`} />
               </div>
               {/* 日期顏色選擇 */}
               <div>
                 <label className={`block text-lg mb-3 ${THEME.date.text} font-bold`}>日期顏色：</label>
                 <div className="grid grid-cols-5 gap-2">
                   {COLORS.map(c => (
                     <ColorButton key={c.value} color={c.value} isActive={dateColor === c.value} onClick={() => { setDateColor(c.value); }} />
                   ))}
                   <label 
                     className={`h-16 rounded-xl border-4 flex items-center justify-center bg-white cursor-pointer ${!isDatePredefinedColor ? 'border-indigo-600 shadow-md' : 'border-[#c2c2c2]'} active:scale-95 transition-transform relative overflow-hidden`}
                   >
                     <input
                       type="color"
                       ref={dateColorInputRef}
                       value={isDatePredefinedColor ? '#000000' : dateColor}
                       onChange={(e) => { setDateColor(e.target.value); }}
                       className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                       style={{ WebkitAppearance: 'none', appearance: 'none', border: 'none' }}
                     />
                     <span className="text-2xl pointer-events-none" style={{ color: !isDatePredefinedColor ? dateColor : '#666' }}>🎨</span>
                   </label>
                 </div>
               </div>
            </section>

            {/* 進階文字設定區 */}
            <section className="space-y-6">
              <div className="flex justify-between items-center">
                <h2 className={`text-2xl text-slate-900 font-black border-l-8 border-slate-900 pl-4`}>⚙️ 進階文字設定</h2>
                <button onClick={() => setShowAdvancedText(!showAdvancedText)} className={`${THEME.advanced.toggleBg} px-6 py-2 rounded-lg`}>{showAdvancedText ? '隱藏 ▲' : '展開 ▼'}</button>
              </div>
              {showAdvancedText && (
                <div className={`${THEME.advanced.bg} p-6 rounded-3xl space-y-6 border-2 ${THEME.advanced.border}`}>
                  <div className="grid grid-cols-2 gap-4">
                    <button onClick={() => setHasGlow(!hasGlow)} className={`p-5 text-xl rounded-xl border-2 ${hasGlow ? THEME.advanced.buttonActive : THEME.advanced.buttonIdle}`}>✨ 精細發光</button>
                    <button onClick={() => setHasShadow(!hasShadow)} className={`p-5 text-xl rounded-xl border-2 ${hasShadow ? THEME.advanced.buttonActive : THEME.advanced.buttonIdle}`}>🌑 文字陰影</button>
                    <button onClick={() => setNoBorder(!noBorder)} className={`p-5 text-xl rounded-xl border-2 ${noBorder ? THEME.advanced.buttonActive : THEME.advanced.buttonIdle}`}>🚫 隱藏描邊</button>
                    <button onClick={() => setIsVertical(!isVertical)} className={`p-5 text-xl rounded-xl border-2 ${isVertical ? THEME.advanced.buttonActive : THEME.advanced.buttonIdle}`}>垂直排列</button>
                  </div>
                  <div className="space-y-4">
                    <label className={`block ${THEME.advanced.text} font-bold`}>📏 祝賀語大小：<input type="range" min="0.5" max="3" step="0.1" value={textScale} onChange={e => setTextScale(Number(e.target.value))} className={`w-full h-10 ${THEME.advanced.accent}`} /></label>
                    <label className={`block ${THEME.advanced.text} font-bold`}>🖋️ 文字描邊粗細：<input type="range" min="0.05" max="0.4" step="0.01" value={borderThickness} onChange={e => setBorderThickness(Number(e.target.value))} className={`w-full h-10 ${THEME.advanced.accent}`} /></label>
                    <label className={`block ${THEME.advanced.text} font-bold`}>↔️ 字距調整：<input type="range" min="0.8" max="2.5" step="0.1" value={letterSpacing} onChange={e => setLetterSpacing(Number(e.target.value))} className={`w-full h-10 ${THEME.advanced.accent}`} /></label>
                  </div>
                </div>
              )}
            </section>

            {/* 進階日期設定區 */}
            <section className="space-y-6">
              <div className="flex justify-between items-center">
                <h2 className={`text-2xl text-slate-900 font-black border-l-8 border-slate-900 pl-4`}>📅 進階日期設定</h2>
                <button onClick={() => setShowAdvancedDate(!showAdvancedDate)} className={`${THEME.advanced.toggleBg} px-6 py-2 rounded-lg`}>{showAdvancedDate ? '隱藏 ▲' : '展開 ▼'}</button>
              </div>
              {showAdvancedDate && (
                <div className={`${THEME.advanced.bg} p-6 rounded-3xl space-y-6 border-2 ${THEME.advanced.border}`}>
                  <div className="grid grid-cols-2 gap-4">
                    <label className={`flex items-center gap-2 ${THEME.advanced.text} p-4 rounded-xl border-2 ${!dateNoBorder ? 'bg-slate-50' : THEME.advanced.buttonIdle}`}>
                      <input type="checkbox" className="w-6 h-6 accent-indigo-600" checked={!dateNoBorder} onChange={(e) => {
                        setDateNoBorder(!e.target.checked);
                      }} />
                      <span className="font-bold">顯示描邊</span>
                    </label>
                  </div>
                  <div className="space-y-4">
                    <label className={`block ${THEME.advanced.text} font-bold`}>📏 日期大小：<input type="range" min="0.5" max="3" step="0.1" value={dateScale} onChange={e => {
                      setDateScale(Number(e.target.value));
                    }} className={`w-full h-10 ${THEME.advanced.accent}`} /></label>
                    <label className={`block ${THEME.advanced.text} font-bold`}>🖋️ 日期描邊粗細：<input type="range" min="0.05" max="0.4" step="0.01" value={dateBorderThickness} onChange={e => {
                      setDateBorderThickness(Number(e.target.value));
                    }} className={`w-full h-10 ${THEME.advanced.accent}`} /></label>
                    <label className={`block ${THEME.advanced.text} font-bold`}>↔️ 日期字距：<input type="range" min="0.8" max="2.5" step="0.1" value={dateLetterSpacing} onChange={e => {
                      setDateLetterSpacing(Number(e.target.value));
                    }} className={`w-full h-10 ${THEME.advanced.accent}`} /></label>
                  </div>
                  <button onClick={() => { 
                    // 重置所有日期進階設定為預設值
                    setDateScale(1);
                    setDateBorderThickness(borderThickness);
                    setDateLetterSpacing(1.1);
                    setDateNoBorder(noBorder);
                  }} className="w-full py-3 bg-slate-200 text-slate-800 rounded-xl text-sm font-black border-2 border-slate-300">✨ 清除日期進階效果 (保留顏色設定)</button>
                </div>
              )}
            </section>

            <div className="pt-10 flex flex-col gap-6">
              <BigButton onClick={() => {
                setIsExporting(true);
                setTimeout(() => {
                  const dataUrl = canvasRef.current?.toDataURL('image/jpeg', 0.9) || '';
                  if (dataUrl) {
                    setExportUrl(dataUrl);
                    // GTM 追踪：成功生成图片
                    if (typeof window !== 'undefined' && (window as any).dataLayer) {
                      // 判断主题颜色
                      let theme = 'custom';
                      const colorMap: Record<string, string> = {
                        '#FFD700': 'gold',
                        '#FF0000': 'red',
                        '#FFFFFF': 'white',
                        '#000000': 'black',
                        '#FF8C00': 'orange',
                        '#008000': 'green',
                        '#00BFFF': 'blue',
                        '#8600FF': 'purple',
                      };
                      if (colorMap[textColor]) {
                        theme = colorMap[textColor];
                      } else if (textColor.startsWith('rainbow')) {
                        theme = 'rainbow';
                      }
                      
                      // 判断文字类型
                      let textType = '自訂';
                      const greetingKeywords = ['早安', '午安', '晚安'];
                      const blessingKeywords = ['平安喜樂', '吉祥如意', '知足常樂', '認同請分享', '健康平安', '天天開心', '心想事成', '福氣滿滿', '祝你愉快', '順心如意', '身體健康', '能量滿滿', '萬事大吉', '好運連連'];
                      
                      if (greetingKeywords.some(keyword => text.includes(keyword))) {
                        textType = '問候';
                      } else if (blessingKeywords.some(keyword => text.includes(keyword))) {
                        textType = '祝福';
                      }
                      
                      (window as any).dataLayer.push({
                        event: 'generate_image',
                        ratio: aspectRatio,
                        theme: theme,
                        text_length: text.length,
                        text_type: textType, // 文字類型：問候 / 祝福 / 自訂
                        has_border: !noBorder, // 是否有邊框（noBorder 為 false 表示有邊框）
                        has_date: showDate // 是否有開啟日期
                      });
                    }
                  }
                  setIsExporting(false);
                }, 500);
              }} variant="success" className="py-10 text-4xl shadow-2xl">{isExporting ? '⏳ 處理中...' : '💾 儲存圖片'}</BigButton>
              <button onClick={() => setImage(null)} className="text-slate-400 text-xl py-4 font-medium">重新上傳照片</button>
            </div>

            {/* 匯出預覽彈窗 (針對所有裝置優化) */}
            {exportUrl && (
              <div className="fixed inset-0 z-50 bg-black/90 flex flex-col items-center justify-center p-6 backdrop-blur-md">
                <div className="max-w-md w-full flex flex-col items-center gap-6">
                  <div className="text-white text-center">
                    <h3 className="text-3xl font-black mb-2">早安圖製作完成</h3>
                    <p className="text-lg opacity-75">【長按圖片】儲存到相簿</p>
                    <p className="text-sm opacity-50 mt-1">或點擊下方按鈕</p>
                  </div>
                  <img 
                    src={exportUrl} 
                    alt="匯出結果" 
                    className="max-w-full max-h-[50vh] rounded-3xl shadow-2xl border-4 border-white/20"
                    style={{ 
                      userSelect: 'none', 
                      WebkitUserSelect: 'none',
                      WebkitTouchCallout: 'default', // iOS 允許長按選單
                      touchAction: 'manipulation'
                    }}
                    draggable={false}
                  />
                  <div className="flex flex-col w-full gap-4">
                   {/* Android LINE 瀏覽器顯示特殊提示 */}
                   {isAndroidLineBrowser ? (
                     <div className="bg-orange-100 border-2 border-orange-500 rounded-3xl p-6 text-center">
                       <div className="text-orange-800 font-black text-xl mb-3">⚠️ 無法儲存圖片</div>
                       <div className="text-orange-700 text-sm mb-4 leading-relaxed">
                         Android LINE 瀏覽器不支援圖片下載功能。<br/>
                         請使用以下方式：
                       </div>
                       <div className="text-orange-800 text-sm space-y-2 mb-4 text-left bg-orange-50 p-4 rounded-xl">
                         <div className="font-bold">方法 1：使用 Google Chrome</div>
                         <div className="text-xs pl-4">1. 點擊右上角「...」→「在瀏覽器中開啟」<br/>2. 選擇「Chrome」或「預設瀏覽器」</div>
                         <div className="font-bold mt-3">方法 2：長按圖片</div>
                         <div className="text-xs pl-4">長按上方預覽圖片，選擇「儲存圖片」或「下載圖片」</div>
                       </div>
                       <button
                         onClick={handleDownload}
                         className="bg-orange-500 text-white py-4 rounded-xl font-black text-lg shadow-lg active:scale-95 transition-transform w-full"
                       >
                         仍要嘗試下載
                       </button>
                     </div>
                   ) : (isMobile && typeof navigator.share === 'function' && !isLineBrowser) ? (
                     <button
                       onClick={handleShare}
                       className="bg-indigo-600 text-white py-6 rounded-3xl font-black text-2xl shadow-lg active:scale-95 transition-transform"
                     >
                       📤 分享圖片 / 儲存
                     </button>
                   ) : (
                     <button
                       onClick={handleDownload}
                       className="bg-indigo-600 text-white py-6 rounded-3xl font-black text-2xl shadow-lg active:scale-95 transition-transform"
                     >
                       {isLineBrowser ? '💾 儲存圖片（新視窗開啟）' : '💾 下載圖片'}
                     </button>
                   )}

                    <button onClick={() => setExportUrl(null)} className="bg-white/10 text-white py-4 rounded-3xl font-bold">返回修改</button>
                  </div>
                </div>
              </div>
            )}

          </>
        )}
      </div>

      {/* 說明頁面 */}
      <HelpModal isOpen={showHelp} onClose={() => setShowHelp(false)} />
      
      {/* 設定頁面 */}
      <SettingsModal 
        isOpen={showSettings} 
        onClose={() => setShowSettings(false)} 
        deferredPrompt={deferredPrompt}
        onInstallComplete={() => setDeferredPrompt(null)}
      />
    </div>
  );
};

export default App;
